import React, { useEffect, useState } from 'react';
import { toast } from 'react-hot-toast';
import activityService, { type ActivityLogItem } from '../../../services/projectManagement/activityService';

interface ActivityFeedPanelProps {
  organizationId: string;
  entityType?: string;
  entityId?: string;
}

const ActivityFeedPanel: React.FC<ActivityFeedPanelProps> = ({ organizationId, entityType, entityId }) => {
  const [items, setItems] = useState<ActivityLogItem[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchActivity = async () => {
    setLoading(true);
    try {
      const { data, error } = await activityService.list({
        organization_id: organizationId,
        entity_type: entityType,
        entity_id: entityId,
        limit: 50,
      });
      if (error) {
        setItems([]);
        toast.error('Failed to load activity');
      } else {
        setItems(data || []);
      }
    } catch {
      setItems([]);
      toast.error('Failed to load activity');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void fetchActivity();
  }, [organizationId, entityType, entityId]);

  const formatWhen = (iso: string) => {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    return d.toLocaleString();
  };

  const summarizeChanges = (changes: Record<string, any>) => {
    const keys = Object.keys(changes || {});
    if (keys.length === 0) return '';
    return keys.slice(0, 3).join(', ') + (keys.length > 3 ? ` +${keys.length - 3}` : '');
  };

  return (
    <div className="rounded-lg border border-gray-200 bg-white p-4">
      <div className="mb-3 flex items-center justify-between">
        <div>
          <div className="text-sm font-semibold text-gray-900">Activity</div>
          <div className="text-xs text-gray-500">Latest updates</div>
        </div>
        <button
          onClick={() => void fetchActivity()}
          className="rounded-lg bg-gray-50 px-3 py-1 text-sm font-medium text-gray-700 hover:bg-gray-100"
        >
          Refresh
        </button>
      </div>

      {loading ? (
        <div className="text-sm text-gray-500">Loading activity...</div>
      ) : items.length === 0 ? (
        <div className="rounded-lg border-2 border-dashed border-gray-200 bg-gray-50 p-6 text-center">
          <div className="text-2xl mb-1">📝</div>
          <div className="text-sm text-gray-600">No activity yet</div>
        </div>
      ) : (
        <div className="space-y-2">
          {items.map((it) => {
            const changes = summarizeChanges(it.changes || {});
            return (
              <div key={it.id} className="rounded-lg border border-gray-200 p-3 hover:bg-gray-50">
                <div className="flex items-center justify-between gap-3">
                  <div className="min-w-0">
                    <div className="text-sm font-medium text-gray-900 truncate">
                      {it.entity_type} {it.action}
                    </div>
                    <div className="mt-0.5 text-xs text-gray-500">
                      {formatWhen(it.created_at)}
                      {changes ? ` • ${changes}` : ''}
                    </div>
                  </div>
                  <div className="text-xs text-gray-400">
                    {it.user_id ? `${it.user_id.slice(0, 8)}…` : ''}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default ActivityFeedPanel;
